import { LoadingArea } from '@consigli/facade';
import { useProjectId, usePackageId, useBlobs, usePackage } from '@consigli/hooks';
import { convertBlobToDocument } from '@consigli/types';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CardBasedFileDisplay,
  SITE_DUE_DILIGENCE_SECTIONS,
} from '@/components/card-based-file-display';
import { DismissibleDisclaimer } from '@/components/dismissible-disclaimer';
import { WaitingForAnalysis } from '@/components/waiting-for-analysis';
import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { useDemoMode } from '@/util/demo-mode';

export const SiteDueDiligenceDocumentsPage: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { adminFeaturesEnabled } = useDemoMode();

  // Fetch document package to check published status
  const { pkg } = usePackage(projectId, packageId);
  const isPublished = pkg ? pkg.published : false;

  // Fetch all blobs (including not analyzed)
  const { blobs, isLoading, isFetching, isError } = useBlobs({
    projectId,
    packageId,
    page: 'all',
  });

  const documents = useMemo(() => {
    return blobs.map((blob) => convertBlobToDocument(blob));
  }, [blobs]);

  const waitingForAnalysis = useMemo(() => {
    if (adminFeaturesEnabled) {
      return false;
    }
    if (isPublished) {
      return false;
    }
    return true;
  }, [isPublished, adminFeaturesEnabled]);

  return (
    <WorkspaceProvider>
      <WorkspaceLayout hideTabs={!packageId || waitingForAnalysis}>
        <Breadcrumb label={t('servicetabs.documents')} />
        {waitingForAnalysis ? (
          <WaitingForAnalysis />
        ) : isLoading ? (
          <LoadingArea title={t('document-list.loading')} />
        ) : isError || documents.length === 0 ? (
          <div className="flex justify-center items-center w-full h-full text-[20px]">
            {t('document-list.no-documents')}
          </div>
        ) : (
          <>
            <DismissibleDisclaimer>{t('optimization-tabs.ai-disclaimer')}</DismissibleDisclaimer>
            <div className="flex">
              <CardBasedFileDisplay
                files={documents}
                isFetching={isFetching}
                isPublished={isPublished}
                sections={SITE_DUE_DILIGENCE_SECTIONS}
              />
            </div>
          </>
        )}
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
